import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { http } from '../../lib/api';
import { toast } from 'sonner';
import { 
  X, 
  FileText, 
  User, 
  Building, 
  Calendar, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Activity,
  Download,
  CloudUpload,
  MapPin,
  Settings,
  Monitor,
  Tag,
  Hash,
  MessageSquare,
  PenTool,
  Loader2
} from 'lucide-react';
import { descargarPdfOrden } from '../../lib/download';
import CerrarOrdenMantenimiento from './CerrarOrdenMantenimiento';
import { resolveDisplayName, buildDisplayNameFromMaybeObject } from '../../lib/displayName';

export default function OrdenDetalleModal({ orden: ordenProp, onClose, onUpdate, resolverNombreTecnico }) {
  const [mostrarCerrarMantenimiento, setMostrarCerrarMantenimiento] = useState(false);
  const [ordenCompleta, setOrdenCompleta] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [subiendoDrive, setSubiendoDrive] = useState(false);

  // Usar la orden completa si ya se cargó, sino la del prop
  const orden = ordenCompleta || ordenProp;

  useEffect(() => {
    // Prevent background scrolling when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Cargar detalle completo (con firmas) bajo demanda
  useEffect(() => {
    const ordenId = ordenProp?._id || ordenProp?.id;
    if (!ordenId) return;
    // Solo cargar si falta la firma (el listado ya no envía firmas)
    if (ordenProp?.firma_cliente !== undefined) return;
    
    const cargarDetalle = async () => {
      setCargandoDetalle(true);
      try {
        const token = localStorage.getItem("token");
        const res = await http.get(`/ordenes/${ordenId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrdenCompleta({ ...ordenProp, ...res.data });
      } catch (err) {
        console.error("Error cargando detalle de orden:", err);
        // Si falla, usamos los datos del listado
      } finally {
        setCargandoDetalle(false);
      }
    };
    cargarDetalle();
  }, [ordenProp]);

  if (!orden) return null;

  const handlePrint = async () => {
    try {
      await descargarPdfOrden(orden._id || orden.id, orden.numero_orden);
    } catch (err) {
      console.error("Error al descargar PDF", err);
      alert("Error al descargar el PDF. Por favor, intente nuevamente.");
    }
  };

  const handleSubirDrive = async () => {
    const ordenId = orden._id || orden.id;
    setSubiendoDrive(true);
    try {
      const token = localStorage.getItem('token');
      const res = await http.post(`/admin/integraciones/drive/upload-orden/${ordenId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = res.data?.url;
      if (url) {
        toast.success(
          <span>PDF subido a GCS. <a href={url} target="_blank" rel="noreferrer" className="underline font-medium">Ver archivo</a></span>
        );
      } else {
        toast.success('PDF subido a Google Cloud Storage');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al subir a Cloud Storage';
      toast.error(msg);
    } finally {
      setSubiendoDrive(false);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    try {
      let fechaStr = orden?.fecha_iso || fecha;
      if (fechaStr.includes('T')) {
        fechaStr = fechaStr.split('T')[0];
      }
      const dateObj = new Date(fechaStr + 'T12:00:00');
      return dateObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  const formatearHora = (hora) => {
    if (!hora) return "--:--";
    if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hora)) return hora;
    if (hora.includes('T')) {
      try {
        const fecha = new Date(hora);
        if (isNaN(fecha.getTime())) return "--:--";
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      } catch (e) {
        return "--:--";
      }
    }
    return "--:--";
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      completada: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completada' },
      en_proceso: { color: 'bg-yellow-100 text-yellow-800', icon: Activity, label: 'En Proceso' },
      pendiente: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Pendiente' }
    };
    
    const config = estados[estado] || { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: estado || 'Pendiente' };
    const Icon = config.icon;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const getTipoServicioTexto = (orden) => {
    if (orden.tipo_servicio) {
      return orden.tipo_servicio === 'mantenimiento_preventivo' ? 'Mantenimiento Preventivo' :
             orden.tipo_servicio === 'correctivo' ? 'Mantenimiento Correctivo' :
             orden.tipo_servicio.charAt(0).toUpperCase() + orden.tipo_servicio.slice(1);
    }
    return orden.es_mantenimiento_preventivo ? 'Mantenimiento Preventivo' : 'Mantenimiento Correctivo';
  };

  const nombreTecnico = useMemo(() => {
    if (resolverNombreTecnico && typeof resolverNombreTecnico === 'function') {
      return resolverNombreTecnico(orden);
    }
    return resolveDisplayName({
      primary: orden?.nombre_tecnico,
      secondary: [
        orden?.tecnico_nombre,
        orden?.tecnico,
        orden?.tecnico_detalle,
        orden?.tecnico_asignado,
        orden?.tecnico_asignado_nombre,
        orden?.usuario_creador_nombre,
        orden?.usuario_creador,
      ],
      lookup: () =>
        buildDisplayNameFromMaybeObject(
          orden?.tecnico_asignado || orden?.tecnico_responsable
        ),
    });
  }, [orden, resolverNombreTecnico]);

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      {mostrarCerrarMantenimiento && (
        <CerrarOrdenMantenimiento
          orden={orden}
          onClose={() => setMostrarCerrarMantenimiento(false)}
          onSuccess={() => {
            if (onUpdate) onUpdate();
            onClose();
          }}
        />
      )}

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div className="text-white">
              <h2 className="text-2xl font-bold mb-1">Orden #{orden.numero_orden}</h2>
              <div className="flex items-center gap-3 text-sm">
                {getEstadoBadge(orden.estado)}
                <span className="text-emerald-100 flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-full">
                  <Settings className="w-3.5 h-3.5" />
                  {getTipoServicioTexto(orden)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {orden.es_servicio_programado && orden.estado !== "completada" && (
              <button
                onClick={() => setMostrarCerrarMantenimiento(true)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2 backdrop-blur-sm border border-white/20"
              >
                <CheckCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Cerrar Mantenimiento</span>
              </button>
            )}
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2 backdrop-blur-sm border border-white/20"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button
              onClick={handleSubirDrive}
              disabled={subiendoDrive}
              title="Subir PDF a Google Cloud Storage"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2 backdrop-blur-sm border border-white/20 disabled:opacity-50 disabled:cursor-wait"
            >
              {subiendoDrive
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <CloudUpload className="w-4 h-4" />}
              <span className="hidden sm:inline">Drive</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Columna Izquierda: Info Principal */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Descripción del Trabajo */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Descripción del Trabajo
                </h3>
                <div className="space-y-4">
                  {orden.trabajos_realizados ? (
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{orden.trabajos_realizados}</p>
                  ) : (
                    <p className="text-gray-400 italic">Sin descripción de trabajos realizados</p>
                  )}
                </div>
              </div>

              {/* Equipos */}
              {orden.equipos && orden.equipos.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-purple-600" />
                    Equipos Atendidos ({orden.equipos.length})
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {orden.equipos.map((equipo, index) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Monitor className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1">
                            {equipo.nombre || equipo.descripcion || 'Sin nombre'}
                          </h4>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                            {equipo.marca && (
                              <span className="flex items-center gap-1">
                                <Tag className="w-3 h-3" /> {equipo.marca}
                              </span>
                            )}
                            {equipo.modelo && (
                              <span className="flex items-center gap-1">
                                <Tag className="w-3 h-3" /> {equipo.modelo}
                              </span>
                            )}
                            {equipo.numero_serie && (
                              <span className="flex items-center gap-1 font-mono bg-gray-100 px-1.5 rounded">
                                <Hash className="w-3 h-3" /> {equipo.numero_serie}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Observaciones */}
              {orden.observaciones && (
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
                  <h3 className="text-lg font-bold text-amber-800 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Observaciones
                  </h3>
                  <p className="text-amber-900 whitespace-pre-wrap leading-relaxed">
                    {orden.observaciones}
                  </p>
                </div>
              )}

              {/* Firma */}
              {cargandoDetalle ? (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <PenTool className="w-5 h-5 text-teal-600" />
                    Conformidad del Cliente
                  </h3>
                  <div className="flex items-center justify-center py-8 text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Cargando firma...
                  </div>
                </div>
              ) : (orden.firma_cliente || orden.aclaracion_firma_cliente) && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <PenTool className="w-5 h-5 text-teal-600" />
                    Conformidad del Cliente
                  </h3>
                  <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-200">
                    {orden.firma_cliente && (
                      <img 
                        src={orden.firma_cliente} 
                        alt="Firma del cliente" 
                        className="max-h-32 mb-4 mix-blend-multiply" 
                      />
                    )}
                    <div className="text-center">
                      <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Aclaración</div>
                      <div className="font-medium text-gray-900 text-lg">
                        {orden.aclaracion_firma || orden.aclaracion_firma_cliente || orden.firma_aclaracion || 'No proporcionada'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Columna Derecha: Metadatos */}
            <div className="space-y-6">
              
              {/* Cliente */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wider">
                  <Building className="w-4 h-4 text-gray-500" />
                  Cliente
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500">Nombre / Razón Social</div>
                    <div className="font-medium text-gray-900">{orden.nombre_cliente || 'No especificado'}</div>
                  </div>
                  {orden.sucursal && (
                    <div className="flex items-start gap-2 pt-2 border-t border-gray-50">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">Sucursal</div>
                        <div className="font-medium text-gray-900">{orden.sucursal}</div>
                      </div>
                    </div>
                  )}
                  {orden.direccion_cliente && (
                    <div className="pt-2 border-t border-gray-50">
                      <div className="text-xs text-gray-500">Dirección</div>
                      <div className="text-sm text-gray-700">{orden.direccion_cliente}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Detalles del Servicio */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wider">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  Agenda
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-gray-500">Fecha Programada</div>
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-emerald-600" />
                      {formatearFecha(orden.fecha_programada || orden.fecha)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Horario</div>
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-emerald-600" />
                      {formatearHora(orden.hora_inicio)} - {formatearHora(orden.hora_fin)}
                    </div>
                  </div>
                  {orden.duracion_formato && orden.duracion_formato !== "Sin registrar" && (
                    <div className="bg-emerald-50 rounded-lg p-3">
                      <div className="text-xs text-emerald-600 font-semibold uppercase">Duración Total</div>
                      <div className="font-bold text-emerald-900 text-lg">
                        {orden.duracion_computada_formato || orden.duracion_formato}
                        {orden.horas_extras_minutos > 0 && (
                          <span className="text-xs font-normal text-orange-500 ml-1">×2</span>
                        )}
                      </div>
                      <div className="text-xs text-emerald-700">
                        ({orden.duracion_computada_decimal ?? orden.duracion_decimal} hrs)
                      </div>
                      {orden.horas_extras_minutos > 0 && (
                        <div className="text-xs text-emerald-400 mt-1">Real: {orden.duracion_formato} ({orden.duracion_decimal} hrs)</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Técnico */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wider">
                  <User className="w-4 h-4 text-gray-500" />
                  Responsable
                </h3>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                    {(nombreTecnico || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{nombreTecnico || 'No asignado'}</div>
                    <div className="text-xs text-gray-500">Técnico Asignado</div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-white px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium border border-gray-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
